import React, { useEffect, useMemo, useState } from "react";
import Icons from "../icons";
import ConfirmModal from "./ConfirmModal";
import HelpButton from "./HelpDialog";
import { JiraConnectForm } from "./JiraConnectDialog";
import { useTheme } from "../theme";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";
const disabledInputCls = "cursor-not-allowed bg-slate-100 text-slate-500";
const labelCls = "mb-1.5 block text-sm font-medium text-slate-700";
const errorTextCls = "mt-1 text-xs text-rose-600";

/* `embedded`: shown as Settings › Profile, under that screen's own header, so
   it has no page header (title, Back, Help) of its own. */
export default function Profile({ onBack, embedded = false }) {
  const { username, email, userid, userkey, setUsername, setEmail } = useTheme();

  const [form, setForm] = useState({
    username: username || "",
    emailid: email || "",
    password: "",
    confirmPassword: "",
  });
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveOk, setSaveOk] = useState(false);

  const initials = getInitials(form.username || username);

  const errors = useMemo(() => {
    const e = {};
    if (form.username.trim().length === 0) e.username = "Display name is required.";
    if (form.emailid.trim().length === 0) e.emailid = "Email is required.";
    else if (!EMAIL_PATTERN.test(form.emailid.trim())) e.emailid = "Enter a valid email address.";
    // Password is optional; validate only when the user is changing it.
    if (form.password.length > 0 || form.confirmPassword.length > 0) {
      if (form.password.length < 8) e.password = "Password must be at least 8 characters.";
      if (form.confirmPassword !== form.password)
        e.confirmPassword = "Passwords do not match.";
    }
    return e;
  }, [form]);

  const canSubmit = Object.keys(errors).length === 0;

  const isDirty =
    form.username.trim() !== (username || "") ||
    form.emailid.trim() !== (email || "") ||
    form.password.length > 0;

  const setField = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setSaveOk(false);
    setSaveError("");
  };
  const markTouched = (key) => setTouched((t) => ({ ...t, [key]: true }));
  const showError = (key) => (touched[key] || submitAttempted) && errors[key];

  const submit = async () => {
    setSubmitAttempted(true);
    if (!canSubmit || saving) return;
    if (!userkey) {
      setSaveError("You are not signed in.");
      return;
    }
    setSaving(true);
    setSaveError("");
    setSaveOk(false);
    try {
      const payload = {
        username: form.username.trim(),
        emailid: form.emailid.trim(),
      };
      if (form.password.length > 0) payload.password = form.password;

      const res = await fetch(`${API_BASE_URL}users/${userkey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);

      // Reflect the changes in the shared app state immediately.
      setUsername(payload.username);
      setEmail(payload.emailid);
      setForm((f) => ({ ...f, password: "", confirmPassword: "" }));
      setSaveOk(true);
    } catch (e) {
      setSaveError(e.message || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  /* The account details and the password, and their Save / Cancel. */
  const details = (
    <>
        {/* Identity banner */}
        <div className="flex items-center gap-4 border-b border-slate-100 px-5 py-5">
          <div className="flex h-14 w-14 flex-none items-center justify-center rounded-full bg-brand-600 text-lg font-semibold text-white">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-slate-900">
              {form.username || username || "Guest"}
            </p>
            <p className="truncate text-sm text-slate-500">{form.emailid || email || "—"}</p>
          </div>
        </div>

        <div className="space-y-5 px-5 py-5">
          {saveError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {saveError}
            </div>
          )}
          {saveOk && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              Profile updated successfully.
            </div>
          )}

          {/* Account section */}
          <div>
            <label className={labelCls}>User ID</label>
            <input value={userid || ""} disabled className={[inputCls, disabledInputCls].join(" ")} />
            <p className="mt-1 text-xs text-slate-400">Your login ID cannot be changed.</p>
          </div>

          <div>
            <label className={labelCls}>Display Name</label>
            <input
              value={form.username}
              onChange={(e) => setField("username", e.target.value)}
              onBlur={() => markTouched("username")}
              placeholder="Full name"
              className={inputCls}
            />
            {showError("username") && <p className={errorTextCls}>{errors.username}</p>}
          </div>

          <div>
            <label className={labelCls}>Email</label>
            <input
              type="email"
              value={form.emailid}
              onChange={(e) => setField("emailid", e.target.value)}
              onBlur={() => markTouched("emailid")}
              placeholder="you@company.com"
              className={inputCls}
            />
            {showError("emailid") && <p className={errorTextCls}>{errors.emailid}</p>}
          </div>

          {/* Password section */}
          <div className="border-t border-slate-100 pt-5">
            <p className="mb-1 text-sm font-semibold text-slate-800">Change password</p>
            <p className="mb-3 text-xs text-slate-400">
              Leave these blank to keep your current password.
            </p>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>New password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setField("password", e.target.value)}
                  onBlur={() => markTouched("password")}
                  placeholder="at least 8 characters"
                  className={inputCls}
                />
                {showError("password") && <p className={errorTextCls}>{errors.password}</p>}
              </div>
              <div>
                <label className={labelCls}>Confirm new password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={form.confirmPassword}
                  onChange={(e) => setField("confirmPassword", e.target.value)}
                  onBlur={() => markTouched("confirmPassword")}
                  placeholder="re-enter new password"
                  className={inputCls}
                />
                {showError("confirmPassword") && (
                  <p className={errorTextCls}>{errors.confirmPassword}</p>
                )}
              </div>
            </div>
          </div>
        </div>
    </>
  );
  const footer = (
    <>
        {/* Footer actions */}
        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={saving || !isDirty || !canSubmit}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
    </>
  );

  /* Settings › Profile: two accordions, each scrolling inside itself, so
     the screen has no outer scroll. Save and Cancel stay in view. */
  if (embedded) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <Accordion id="settings-user-details" title="User details" Icon={Icons.UserCircle} footer={footer}>
          {details}
        </Accordion>
        {userkey && (
          <Accordion id="settings-jira" title="Jira connectivity" Icon={Icons.Link}>
            <JiraAccountSection userkey={userkey} bare />
          </Accordion>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"
          >
            <Icons.ArrowLeft className="h-4 w-4" />
            Back
          </button>
        )}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-slate-900">Edit profile</h1>
            <p className="mt-1 text-sm text-slate-500">
              Update your account details and password.
            </p>
          </div>
          <HelpButton screen="profile" />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
        {details}
        {footer}
      </div>

      {userkey && <JiraAccountSection userkey={userkey} />}
    </div>
  );
}

/* One section of Settings › Profile: a header that opens and closes it, and
   a body that scrolls inside itself. Open, it shares the height with the
   other open one; `footer` stays under the body, out of the scroll. */
function Accordion({ id, title, Icon, footer, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm ${open ? "flex-1" : "flex-none"}`}
    >
      <h2 className="flex-none">
        <button
          type="button"
          id={`${id}-header`}
          aria-expanded={open}
          aria-controls={`${id}-panel`}
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-2 px-5 py-3.5 text-left text-base font-semibold text-slate-900 transition hover:bg-slate-50"
        >
          <Icon className="h-4 w-4 flex-none text-brand-700" aria-hidden="true" />
          <span className="flex-1">{title}</span>
          <Icons.ChevronDown className={`h-4 w-4 flex-none text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </h2>
      {open && (
        <>
          <div
            id={`${id}-panel`}
            role="region"
            aria-labelledby={`${id}-header`}
            className="cq-scroll min-h-0 flex-1 overflow-y-auto border-t border-slate-100"
          >
            {children}
          </div>
          {footer && <div className="flex-none">{footer}</div>}
        </>
      )}
    </section>
  );
}

/* The user's Jira: their Jira email and own API token, given once here (or
   on For you when not given here). Saved on its own, apart from the profile. */
/* `bare`: inside Settings › Profile's own accordion, with no card or title. */
export function JiraAccountSection({ userkey, bare = false }) {
  const [link, setLink] = useState(null);
  const [error, setError] = useState("");
  const [changing, setChanging] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}for-you/jira-link?userkey=${encodeURIComponent(userkey)}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
        if (!gone) setLink(body?.data || null);
      } catch (e) {
        if (!gone) setError(e.message || "Could not read your Jira connection.");
      }
    })();
    return () => {
      gone = true;
    };
  }, [userkey]);

  const disconnect = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}for-you/jira-link?userkey=${encodeURIComponent(userkey)}`, { method: "DELETE" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setLink(body?.data || null);
      setChanging(false);
    } catch (e) {
      setError(e.message || "Could not disconnect Jira.");
    } finally {
      setBusy(false);
      setConfirmOff(false);
    }
  };

  const ready = !!link?.connected && !!link?.ready;
  const intro =
    "Your Jira email and your own Jira API token. For you lists what Jira assigns you, and your test runs are " +
    "started, finished and filled in Jira with your own rights.";
  const Wrapper = bare ? "div" : "section";
  return (
    <Wrapper
      {...(bare
        ? {}
        : { "aria-labelledby": "profile-jira-title", className: "mt-6 overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm" })}
    >
      {bare ? (
        <p className="px-5 pt-4 text-sm text-slate-500">{intro}</p>
      ) : (
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 id="profile-jira-title" className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <Icons.Link className="h-4 w-4 text-brand-700" /> Jira account
          </h2>
          <p className="mt-1 text-sm text-slate-500">{intro}</p>
        </div>
      )}
      <div className="px-5 py-5">
        {error && <p role="alert" className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
        {link && ready && !changing ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-sm text-slate-700">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                <Icons.Check className="h-3.5 w-3.5" /> Connected
              </span>
              {link.display_name}
              {link.email ? ` (${link.email})` : ""}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setChanging(true)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Change
              </button>
              <button
                type="button"
                onClick={() => setConfirmOff(true)}
                className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : link ? (
          <JiraConnectForm
            userkey={userkey}
            idPrefix="profile-jira"
            onCancel={changing ? () => setChanging(false) : undefined}
            onConnected={(l) => {
              setLink(l);
              setChanging(false);
              setError("");
            }}
          />
        ) : null}
      </div>
      {confirmOff && (
        <ConfirmModal
          title="Disconnect Jira?"
          message="For you will stop listing your Jira work, your saved Jira token is deleted, and test runs can't be worked until you connect again."
          confirmLabel="Disconnect"
          busyLabel="Disconnecting…"
          busy={busy}
          icon={Icons.Unlink}
          onCancel={() => setConfirmOff(false)}
          onConfirm={disconnect}
        />
      )}
    </Wrapper>
  );
}
